<template>
  <!-- 宿主元素：净化后的 HTML 会被挂进它的 shadowRoot，样式被 Shadow 边界隔离，不污染主文档 -->
  <div ref="host" class="safe-html-host"></div>
</template>

<script>
import DOMPurify from 'dompurify'
import { scanAndRenderMarkdownWidgets } from '@/utils/markdownRenderer'

/**
 * SafeHtmlRenderer
 * 用于安全渲染由大模型生成的 HTML 片段：
 * 1. 通过 DOMPurify 去除 <script>、事件属性、javascript: 等，杜绝 XSS；
 * 2. 保留 <style> / style（报告自带的配色排版），但挂进 Shadow DOM，
 *    使其样式被 Shadow 边界隔离，既不污染外部页面，外部（含 Element UI）也不侵入。
 *
 * 注意：Shadow DOM 无法被 html2canvas/html2pdf 穿透，下载 PDF 时不能截取 shadowRoot，
 * 需使用净化后的普通 DOM（见 getSanitizedHtml / 下载逻辑）。
 */
export default {
  name: 'SafeHtmlRenderer',
  props: {
    html: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      shadowRoot: null,
      // 缓存最近一次净化后的 HTML，供父组件下载时复用（保证所见即所得）
      sanitizedHtml: ''
    }
  },
  watch: {
    html() {
      this.render()
    }
  },
  mounted() {
    this.render()
  },
  methods: {
    // DOMPurify 净化配置：保留 style，禁止脚本与危险属性
    sanitize(raw) {
      return DOMPurify.sanitize(raw || '', {
        // 保留报告样式相关能力 + checkbox 渲染所需标签/属性
        ADD_TAGS: ['style', 'input'],
        ADD_ATTR: [
          'target', 'type', 'checked', 'disabled', 'aria-label',
          // markdownRenderer fence widgets：保留 mermaid / echarts 的 data 属性
          'data-mermaid', 'data-chart-escaped', 'data-mermaid-rendered', 'data-echarts-rendered'
        ],
        // 明确禁止脚本及可执行内容
        FORBID_TAGS: ['script'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
        // 禁止 data:/javascript: 等可执行协议的资源加载（图片除外由默认策略处理）
        ALLOW_UNKNOWN_PROTOCOLS: false
      })
    },
    render() {
      const host = this.$refs.host
      if (!host) return

      // 惰性创建 shadowRoot（同一元素只能 attach 一次）
      if (!this.shadowRoot) {
        this.shadowRoot = host.attachShadow({ mode: 'open' })
        // 注入全局 widget 基础样式（md-echarts-block、mermaid-block 等）
        // Shadow DOM 隔离后，外部全局样式不穿透进来，必须显式注入
        const widgetStyle = document.createElement('style')
        widgetStyle.textContent = `
          .md-echarts-block { margin: 12px 0; border-radius: 8px; overflow: hidden; background: transparent; }
          .md-echarts-block__body { width: 100%; min-height: 280px; }
          .echarts-error { color: #e5484d; padding: 8px; font-size: 13px; }
          .mermaid-block { margin: 12px 0; }
        `
        this.shadowRoot.appendChild(widgetStyle)
      }

      this.sanitizedHtml = this.sanitize(this.html)
      this.shadowRoot.innerHTML = this.sanitizedHtml

      // Shadow DOM 边界会屏蔽全局 MutationObserver，需主动扫描 mermaid / task list 等 widget
      try {
        scanAndRenderMarkdownWidgets(this.shadowRoot)
      } catch (_e) {
        /* 扫描失败不影响基础 HTML 展示 */
      }

      // 渲染完成后向父组件回传净化结果，便于下载等场景复用
      this.$emit('rendered', this.sanitizedHtml)
    },
    // 供父组件调用：拿到当前净化后的 HTML 字符串
    getSanitizedHtml() {
      return this.sanitizedHtml
    }
  }
}
</script>

<style scoped>
.safe-html-host {
  /* 宿主本身不产生额外样式，内容样式由 shadowRoot 内的 <style> 决定 */
  display: block;
  width: 100%;
}
</style>
